/**
 * Pending Post Service
 * Handles storage and retrieval of pending posts for unauthorized users
 * Uses IndexedDB for files and sessionStorage for metadata
 */

const DB_NAME = 'WantInSummerDB';
const DB_VERSION = 2;
const STORE_NAME = 'pendingPostFiles';
const SESSION_STORAGE_KEY = 'pendingPostData';

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains('pendingTripFiles')) {
        db.createObjectStore('pendingTripFiles');
      }
    };
  });
};

// Convert File to ArrayBuffer for storage
const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

// Convert ArrayBuffer back to File
const arrayBufferToFile = (arrayBuffer, fileName, fileType) => {
  return new File([arrayBuffer], fileName, { type: fileType });
};

/**
 * Save pending post data and files
 * @param {Object} postData - Post form data
 * @param {File[]} files - Array of image files
 * @returns {Promise<void>}
 */
export const savePendingPost = async (postData, files = []) => {
  try {
    // Save files to IndexedDB
    const db = await initDB();
    const fileKeys = [];
    
    const saveFilePromises = files.map(async (file, index) => {
      const fileKey = `file_${Date.now()}_${index}`;
      const arrayBuffer = await fileToArrayBuffer(file);
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({
          arrayBuffer,
          fileName: file.name,
          fileType: file.type,
          lastModified: file.lastModified
        }, fileKey);
        
        request.onsuccess = () => {
          fileKeys.push(fileKey);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(saveFilePromises);

    // Save metadata to sessionStorage
    const metadata = {
      postData,
      fileKeys,
      timestamp: Date.now()
    };

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(metadata));
    
    return { success: true };
  } catch (error) {
    console.error('Error saving pending post:', error);
    throw new Error('Не удалось сохранить пост. Попробуйте ещё раз.');
  }
};

/**
 * Get pending post data and files
 * @returns {Promise<{postData: Object, files: File[]} | null>}
 */
export const getPendingPost = async () => {
  try {
    const metadataStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!metadataStr) {
      return null;
    }

    const metadata = JSON.parse(metadataStr);
    const { postData, fileKeys } = metadata;

    // Check if data is too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (Date.now() - metadata.timestamp > maxAge) {
      await clearPendingPost();
      return null;
    }

    // Retrieve files from IndexedDB
    const db = await initDB();
    const files = [];

    const getFilePromises = fileKeys.map(async (fileKey) => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(fileKey);

        request.onsuccess = () => {
          const fileData = request.result;
          if (fileData) {
            const file = arrayBufferToFile(
              fileData.arrayBuffer,
              fileData.fileName,
              fileData.fileType
            );
            files.push(file);
          }
          resolve();
        };

        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(getFilePromises);

    return { postData, files };
  } catch (error) {
    console.error('Error retrieving pending post:', error);
    return null;
  }
};

/**
 * Clear pending post data and files
 * @returns {Promise<void>}
 */
export const clearPendingPost = async () => {
  try {
    // Get metadata before clearing
    const metadataStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
    
    // Clear sessionStorage
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    // Clear IndexedDB files if metadata existed
    if (metadataStr) {
      try {
        const metadata = JSON.parse(metadataStr);
        const { fileKeys } = metadata;

        if (fileKeys && fileKeys.length > 0) {
          const db = await initDB();
          const transaction = db.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);

          fileKeys.forEach((fileKey) => {
            store.delete(fileKey);
          });

          await new Promise((resolve, reject) => {
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
          });
        }
      } catch (parseError) {
        console.error('Error parsing metadata during clear:', parseError);
      }
    }
  } catch (error) {
    console.error('Error clearing pending post:', error);
    // Don't throw - clearing is best effort
  }
};

/**
 * Check if pending post exists
 * @returns {boolean}
 */
export const hasPendingPost = () => {
  return !!sessionStorage.getItem(SESSION_STORAGE_KEY);
};

